// src/components/OnlineUsers.tsx
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../api/client';
import { useAuth } from '../contexts/AuthContext';

interface OnlineUser {
  id: string;
  name: string;
  photo?: string;
}

const OnlineUsers: React.FC = () => {
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [showPopup, setShowPopup] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const hideTimerRef = useRef<number | null>(null);
  const { user: currentUser } = useAuth();

  const fetchOnlineUsers = async () => {
    try {
      const res = await api.get('/users/online');
      const filtered = res.data.filter((u: OnlineUser) => u.id !== currentUser?.id);
      setOnlineUsers(filtered);
    } catch (err) {
      console.error('Ошибка загрузки онлайн-пользователей', err);
    }
  };

  useEffect(() => {
    fetchOnlineUsers();
    const interval = setInterval(fetchOnlineUsers, 30000);
    return () => clearInterval(interval);
  }, []);

  const clearHideTimer = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const showWithTimer = () => {
    clearHideTimer();
    setShowPopup(true);
  };

  const hideWithDelay = () => {
    hideTimerRef.current = window.setTimeout(() => {
      setShowPopup(false);
    }, 200);
  };

  // Закрываем popup при клике вне контейнера и popup
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current && !containerRef.current.contains(event.target as Node) &&
        popupRef.current && !popupRef.current.contains(event.target as Node)
      ) {
        setShowPopup(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (onlineUsers.length === 0) return null;

  const displayedUsers = onlineUsers.slice(0, 5);
  const remainingCount = onlineUsers.length - displayedUsers.length;

  const rect = containerRef.current?.getBoundingClientRect();
  const popupStyle: React.CSSProperties = rect ? {
    position: 'fixed',
    top: rect.bottom + 10,
    right: window.innerWidth - rect.right,
    width: 260,
    backgroundColor: 'white',
    borderRadius: 16,
    boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
    border: '1px solid rgba(0,0,0,0.05)',
    zIndex: 99999,
    overflow: 'hidden',
  } : {};

  const popupElement = showPopup && onlineUsers.length > 0 && (
    <div
      ref={popupRef}
      style={popupStyle}
      onMouseEnter={showWithTimer}
      onMouseLeave={hideWithDelay}
    >
      <div style={styles.popupHeader}>
        <span>Сейчас в сети</span>
        <span style={styles.popupCount}>{onlineUsers.length}</span>
      </div>
      <div style={styles.popupList}>
        {onlineUsers.map(user => (
          <div key={user.id} style={styles.popupUser}>
            <div style={styles.popupAvatar}>
              {user.photo ? (
                <img src={user.photo} alt={user.name} style={styles.avatarImage} />
              ) : (
                <span>{user.name[0]}</span>
              )}
              <span style={styles.popupOnlineDot} />
            </div>
            <span style={styles.popupName}>{user.name}</span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div style={styles.container} ref={containerRef}>
      <div
        style={styles.avatarsGroup}
        onMouseEnter={showWithTimer}
        onMouseLeave={hideWithDelay}
      >
        {displayedUsers.map((user, index) => (
          <div
            key={user.id}
            style={{
              ...styles.avatar,
              zIndex: 5 - index,
              marginLeft: index === 0 ? 0 : -8,
            }}
            title={user.name}
          >
            {user.photo ? (
              <img src={user.photo} alt={user.name} style={styles.avatarImage} />
            ) : (
              <span style={styles.avatarInitial}>{user.name[0]}</span>
            )}
            <span style={styles.onlineDot} />
          </div>
        ))}
        {remainingCount > 0 && (
          <div style={styles.remainingBadge} title={`Ещё ${remainingCount} онлайн`}>
            +{remainingCount}
          </div>
        )}
      </div>

      {popupElement && createPortal(popupElement, document.body)}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'relative',
    display: 'inline-block',
  },
  avatarsGroup: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    position: 'relative',
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: '#2575fc',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px solid white',
    overflow: 'hidden',
    position: 'relative',
    transition: 'transform 0.2s',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  avatarInitial: {
    fontSize: 14,
    fontWeight: 600,
  },
  onlineDot: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: 10,
    height: 10,
    backgroundColor: '#28a745',
    borderRadius: '50%',
    border: '2px solid white',
    boxShadow: '0 0 0 1px rgba(0,0,0,0.05)',
  },
  remainingBadge: {
    marginLeft: 5,
    fontSize: 12,
    fontWeight: 500,
    color: '#2575fc',
    backgroundColor: 'rgba(37,117,252,0.1)',
    borderRadius: 20,
    padding: '2px 8px',
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  popupHeader: {
    padding: '12px 16px',
    borderBottom: '1px solid #f0f0f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontWeight: 600,
    fontSize: 14,
    color: '#1a1a2e',
  },
  popupCount: {
    backgroundColor: '#e6f0fa',
    color: '#2575fc',
    borderRadius: 20,
    padding: '2px 8px',
    fontSize: 12,
  },
  popupList: {
    maxHeight: 300,
    overflowY: 'auto',
    padding: '8px 0',
  },
  popupUser: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 16px',
    gap: 12,
    cursor: 'default',
    transition: 'background 0.2s',
  },
  popupAvatar: {
    position: 'relative',
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: '#2575fc',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  popupOnlineDot: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: 8,
    height: 8,
    backgroundColor: '#28a745',
    borderRadius: '50%',
    border: '2px solid white',
  },
  popupName: {
    fontSize: 14,
    color: '#333',
    fontWeight: 500,
  },
};

export default OnlineUsers;